import {isNumber, isObject, isString, toNumber} from 'lodash';
import {Subject} from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeAll,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {BugsnagSeveritiesEnum} from '../../../libs/shared/helpers/src/bugsnag/bugsnagSeverities';
import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {
  AttachmentSizeError,
  CustomError,
  ExtendedError,
} from '../../../libs/shared/helpers/src/types/customErrors';
import {CancelError} from '../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {ApiBadResponseError, IOTSError} from '../../../src/helpers/apiHelpers';
import {isAbortError} from '../../../src/helpers/idbHelpers';
import {HttpError, HttpResponseError} from '../../../src/services/http/httpError';
import {isFrivolousResponseBody} from '../../../src/services/http/httpHelpers';
import {networkMonitor} from '../../../src/services/network/networkMonitor';
import {isOnline} from '../../helpers/networkReachabilityHelpers';
import {bufferUnlessEmptyWhen} from '../../rx/bufferUnlessEmptyWhen';
import {Event} from './bugsnagClient';

/*
 * Types.
 */

export interface BugsnagReport {
  error: Error | {name: string; message: string};
  options?: ReportToBugsnagOptions;
  callback?: () => void;
}

interface ReportToBugsnagOptions {
  severity?: BugsnagSeveritiesEnum;
  metadata?: {[key: string]: any};
}

/*
 * Helpers.
 */

export function buildBugsnagReportingPipeline(reports$: Subject<BugsnagReport>) {
  if (isMobile()) {
    // On mobile, just report immediately since Bugsnag will handle errors that were reported when offline.
    return reports$;
  }

  // On web, use the network monitor to report only when online.
  const online$ = networkMonitor.isOnline$.pipe(filter((isNetworkOnline) => isNetworkOnline));
  const offline$ = networkMonitor.isOnline$.pipe(filter((isNetworkOnline) => !isNetworkOnline));
  return networkMonitor.isOnline$.pipe(
    startWith(isOnline()),
    distinctUntilChanged(),
    concatMap((isNetworkOnline) => {
      if (isNetworkOnline) {
        // While online, pass the reports through until we're offline.
        return reports$.pipe(takeUntil(offline$));
      }

      // While offline, buffer the reports until we're online.
      return reports$.pipe(
        // Execute the completion callback immediately.
        tap((report) => report.callback?.()),
        bufferUnlessEmptyWhen(() => online$),
        mergeAll(),
        map((report) => ({...report, callback: undefined})),
      );
    }),
  );
}

export function onBugsnagError(event: Event) {
  // Unhandled errors must always be reported on mobile.
  const isHardCrash = isMobile() && event.unhandled && !isUnhandledPromiseRejection(event);

  // Ignore some errors to avoid spamming Bugsnag.
  if (shouldIgnoreError(event.originalError) && !isHardCrash) {
    return false;
  }

  maybeOverrideGroupingHash(event);
  maybeOverrideErrorClass(event);
  maybeAddMetadata(event);
  maybeFixErrorMessage(event);

  return true;
}

export function findReportSeverity(event: Event): BugsnagSeveritiesEnum {
  if (event.unhandled) {
    return event.severity as BugsnagSeveritiesEnum;
  }

  const {originalError} = event;

  const isBootCancelError =
    originalError instanceof ExtendedError && originalError.originalError instanceof CancelError;
  if (!hasUserProps(event) && isBootCancelError) {
    return BugsnagSeveritiesEnum.INFO;
  }

  const httpErrorCode = originalError instanceof HttpResponseError && Number(originalError.code);
  const canIgnoreHttpError = httpErrorCode >= 200 && httpErrorCode < 500;

  // Before boot, rednering, OR io-ts => critical error.
  if (
    (!hasUserProps(event) && !canIgnoreHttpError) ||
    isRenderingError(event) ||
    originalError instanceof IOTSError
  ) {
    return BugsnagSeveritiesEnum.ERROR;
  }

  // Http error => info.
  if (originalError instanceof HttpError) {
    if (httpErrorCode === 403) {
      return BugsnagSeveritiesEnum.WARNING;
    }

    return BugsnagSeveritiesEnum.INFO;
  }

  // IDB Abort errors, not much we can do about those :/
  if (isAbortError(originalError)) {
    return BugsnagSeveritiesEnum.INFO;
  }

  // Otherwise, warning.
  return BugsnagSeveritiesEnum.WARNING;
}

function shouldIgnoreError(error: unknown) {
  if (error instanceof HttpError && shouldIgnoreHttpError(error)) {
    return true;
  }

  // PB-11432: This can be removed when desktop app version 3.10 is no
  // longer being used.
  if (
    isObject(error) &&
    'message' in error &&
    isString(error.message) &&
    error.message.includes('Unable to send AtomFrameHostMsg_Message_Sync')
  ) {
    return true;
  }

  // Skip reporting ResizeObserver loop error.
  if (
    isString(error) &&
    (error === 'ResizeObserver loop completed with undelivered notifications.' ||
      error === 'ResizeObserver loop limit exceeded')
  ) {
    return true;
  }

  if (error instanceof ApiBadResponseError && isFrivolousResponseBody(error.body)) {
    return true;
  }

  if (error instanceof CancelError) {
    return true;
  }

  if (error instanceof AttachmentSizeError) {
    return true;
  }

  if (error instanceof ExtendedError) {
    return error.originalError instanceof CancelError;
  }

  return false;
}

function shouldIgnoreHttpError(error: HttpError) {
  // Ignore logged errors.
  if (error.isLogged) {
    return true;
  }

  return false;
}

function maybeOverrideGroupingHash(event: Event) {
  const {originalError} = event;
  if (!(originalError instanceof CustomError)) {
    return;
  }

  const {groupingHash} = originalError;
  if (!groupingHash) {
    return;
  }

  // eslint-disable-next-line no-param-reassign
  event.groupingHash = groupingHash;
}

function maybeOverrideErrorClass(event: Event) {
  const {originalError} = event;
  if (!(originalError instanceof CustomError)) {
    return;
  }

  // eslint-disable-next-line no-param-reassign
  event.errors[0].errorClass = originalError.constructor.name;
}

function maybeAddMetadata(event: Event) {
  const {originalError} = event;

  if (!(originalError instanceof CustomError)) {
    return;
  }

  const serializableError = originalError.toSerializableObject();
  event.addMetadata('Error details', serializableError || {...event.originalError});
}

function maybeFixErrorMessage(event: Event) {
  event.errors.forEach((error) => {
    const promiseMetadata = event.getMetadata('custom', 'promise');
    if (error.errorMessage.includes(`"Promise"`) && promiseMetadata['rejection reason']) {
      // eslint-disable-next-line no-param-reassign
      error.errorMessage = String(promiseMetadata['rejection reason'] || '');
    }
  });
}

function hasUserProps(event: Event) {
  const userId = toNumber(event.getUser().id);
  const companyId = event.getMetadata('user', 'companyId');
  return isNumber(companyId) && isNumber(userId);
}

function isRenderingError(event: Event) {
  return event.getMetadata('front', 'level') === 'render';
}

function isUnhandledPromiseRejection(event: Event) {
  const internalEvent = event as any;
  // eslint-disable-next-line no-underscore-dangle
  const {severityReason} = internalEvent._handledState;

  if (!severityReason && isDevelopment()) {
    throw new Error('Bugsnag internal implementation has changed. You need to update this function');
  }

  return severityReason.type === 'unhandledPromiseRejection';
}
